import { useMutation, useQuery } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, CircularProgress } from '@mui/material';
import { useUuid, useCustomSnackbar } from '@procureai/ui-mui';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  IntakeRequestDetailsQuery,
  SvbrgChatMessageStatus,
  AstroUserProfile,
  SvbrgFormStepType,
} from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import { ChatMessageProps } from '~/common/types/ChatMessage';
import { ChatActionNext } from '~/components/ChatStep/ChatActions/ChatActionNext';
import { ChatStep } from '~/components/ChatStep/ChatStep';
import { useUser } from '~/hooks/useUser/useUser';
import { useChatSession } from '~/modules';
import { usePrefillDeliveryLocation } from '~/routes/IntakeDetail/hooks/UsePrefillDeliveryLocation';
import {
  useExtractedRequirements,
  shouldPrePopulateFromRequirements,
} from '~/routes/IntakeDetail/utils/useExtractedRequirements';
import { mergeOverrideNull } from '~/util/object';

import { IntakeRequestDetailsForm } from './components/IntakeRequestDetailsForm';
import { INTAKE_REQUEST_DETAILS_STEP_MUTATION } from './IntakeRequestDetailsStep.mutation.gql';
import { INTAKE_REQUEST_DETAILS } from './IntakeRequestDetailsStep.query.gql';
import {
  IntakeRequestDetailsStepFormData,
  intakeRequestDetailsStepFormDataDefault,
  intakeRequestDetailsStepFormDataSchema,
} from './IntakeRequestDetailsStep.schema';

type IntakeRequestDetailsFormWrapperProps = {
  data: IntakeRequestDetailsQuery;
  formId: string;
  disabled?: boolean;
  profile: AstroUserProfile;
};

const IntakeRequestDetailsFormWrapper = ({ data, profile, formId, disabled }: IntakeRequestDetailsFormWrapperProps) => {
  const { onError } = useCustomSnackbar();
  const session = useChatSession();

  // Get extracted requirements for this step
  const { hasData: hasExtractedData, data: extractedData } = useExtractedRequirements({
    chatSessionId: session?.id || '',
    stepType: SvbrgFormStepType.REQUEST_DETAILS,
    enabled: Boolean(session?.id),
  });

  const form = useForm<IntakeRequestDetailsStepFormData>({
    mode: 'onTouched',
    resolver: zodResolver(intakeRequestDetailsStepFormDataSchema),
    defaultValues: {
      ...intakeRequestDetailsStepFormDataDefault,
      organizationId: profile.organizationId,
      currency: profile.currency,
      deliveryLocationId: profile.deliveryLocationId,
      accountingAreaId: profile.accountingAreaId,
      costCenterId: profile.costCenterId,
    },
  });

  usePrefillDeliveryLocation(form);

  useEffect(() => {
    const request = data.sevenbridges.chatSession.request;

    // Priority 1: Use existing request data if available
    if (request) {
      // This is a workaround because the zod schema does not handle null values
      form.reset(
        mergeOverrideNull(
          {
            ...intakeRequestDetailsStepFormDataDefault,
            organizationId: profile.organizationId,
            currency: profile.currency,
            deliveryLocationId: profile.deliveryLocationId,
            accountingAreaId: profile.accountingAreaId,
            costCenterId: profile.costCenterId,
          },
          request,
        ),
      );
      return;
    }

    // Priority 2: Pre-populate from extracted requirements if available and no existing data
    if (shouldPrePopulateFromRequirements(hasExtractedData, request)) {
      const baseDefaults = {
        ...intakeRequestDetailsStepFormDataDefault,
        organizationId: profile.organizationId,
        currency: profile.currency,
        deliveryLocationId: profile.deliveryLocationId,
        accountingAreaId: profile.accountingAreaId,
        costCenterId: profile.costCenterId,
      };

      // extractedData is already pre-processed by the backend
      const prePopulatedData = {
        ...baseDefaults,
        ...extractedData, // Backend already provides the right structure
      };
      form.reset(prePopulatedData);
    }
  }, [form, profile, data, hasExtractedData, extractedData]);

  const [save] = useMutation(INTAKE_REQUEST_DETAILS_STEP_MUTATION, {
    onError,
  });

  return (
    <FormProvider {...form}>
      <form
        id={formId}
        onSubmit={form.handleSubmit(
          (data) => {
            save({
              variables: { data: { ...data, chatSessionId: session.id } },
            });
          },
          (error) => console.log(error),
        )}
        autoComplete="off"
        noValidate
      >
        <IntakeRequestDetailsForm disabled={disabled} />
      </form>
    </FormProvider>
  );
};

export const IntakeRequestDetailsStep = ({ role, status, createdBy, chatSessionCompleted }: ChatMessageProps) => {
  const { t: tGlobal } = useTranslation(I18nNamespace.intake);
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: 'IntakeRequestDetailsStep' });
  const { profile } = useUser({ userId: createdBy });
  const session = useChatSession();
  const formId = useUuid();

  const { data, loading } = useQuery(INTAKE_REQUEST_DETAILS, {
    variables: session,
  });

  const isLoading = loading || status === SvbrgChatMessageStatus.PENDING;

  const disabled = chatSessionCompleted || isLoading;
  return (
    <ChatStep
      title={tGlobal('IntakeChatStep.assistant')}
      role={role}
      createdBy={createdBy}
      message={t('message')}
      footer={<ChatActionNext formId={formId} disabled={disabled} />}
    >
      {isLoading ? (
        <CircularProgress />
      ) : (
        <Box
          sx={{
            paddingTop: 2,
          }}
        >
          {data && profile && (
            <IntakeRequestDetailsFormWrapper
              data={data}
              profile={profile}
              formId={formId}
              disabled={chatSessionCompleted}
            />
          )}
        </Box>
      )}
    </ChatStep>
  );
};
